import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Chip, CircularProgress,
  IconButton, Tooltip, Typography,
} from '@mui/material';
import { AutoAwesome, Whatshot } from '@mui/icons-material';
import { workforceApi } from '../services/hrmsApi';
import { useChatContext } from '../context/ChatContext';

// ── Risk colour scale ─────────────────────────────────────────────────────────
function getRiskLevel(score) {
  if (score >= 70) return 'critical';
  if (score >= 50) return 'high';
  if (score >= 30) return 'medium';
  return 'low';
}

const RISK = {
  critical: { bg: '#FFCDD2', text: '#B71C1C', chipBg: '#FFEBEE', chipText: '#C62828', label: 'Critical' },
  high:     { bg: '#FFE0B2', text: '#BF360C', chipBg: '#FFF3E0', chipText: '#E65100', label: 'High' },
  medium:   { bg: '#FFF9C4', text: '#F57F17', chipBg: '#FFFDE7', chipText: '#F9A825', label: 'Medium' },
  low:      { bg: '#E8F5E9', text: '#2E7D32', chipBg: '#F1F8E9', chipText: '#388E3C', label: 'Low' },
};

// ── Stub data ─────────────────────────────────────────────────────────────────
const STUB = {
  riskSummary: 'Engineering and Sales have the highest attrition risk, concentrated in the 1–2 year tenure band. Targeted retention programmes are recommended before Q3.',
  tenureBands: ['< 1 yr', '1-2 yr', '2-5 yr', '5-10 yr', '10+ yr'],
  rows: [
    { department: 'Engineering', overallRiskScore: 72, cells: [{ riskScore: 80, headcount: 8  }, { riskScore: 75, headcount: 12 }, { riskScore: 50, headcount: 25 }, { riskScore: 25, headcount: 18 }, { riskScore: 12, headcount: 9  }] },
    { department: 'Sales',       overallRiskScore: 56, cells: [{ riskScore: 72, headcount: 14 }, { riskScore: 65, headcount: 22 }, { riskScore: 38, headcount: 45 }, { riskScore: 18, headcount: 31 }, { riskScore: 10, headcount: 18 }] },
    { department: 'IT',          overallRiskScore: 51, cells: [{ riskScore: 58, headcount: 11 }, { riskScore: 70, headcount: 18 }, { riskScore: 45, headcount: 32 }, { riskScore: 22, headcount: 27 }, { riskScore: 8,  headcount: 14 }] },
    { department: 'Operations',  overallRiskScore: 42, cells: [{ riskScore: 45, headcount: 19 }, { riskScore: 52, headcount: 28 }, { riskScore: 30, headcount: 48 }, { riskScore: 14, headcount: 35 }, { riskScore: 6,  headcount: 22 }] },
    { department: 'Finance',     overallRiskScore: 30, cells: [{ riskScore: 30, headcount: 7  }, { riskScore: 42, headcount: 11 }, { riskScore: 25, headcount: 19 }, { riskScore: 10, headcount: 14 }, { riskScore: 4,  headcount: 10 }] },
    { department: 'HR',          overallRiskScore: 24, cells: [{ riskScore: 28, headcount: 5  }, { riskScore: 35, headcount: 9  }, { riskScore: 18, headcount: 15 }, { riskScore: 8,  headcount: 12 }, { riskScore: 3,  headcount: 8  }] },
  ],
};

// ── Sub-components ────────────────────────────────────────────────────────────
function HeatCell({ riskScore, headcount }) {
  const level = getRiskLevel(riskScore);
  const rs    = RISK[level];
  return (
    <Tooltip
      title={`Risk: ${riskScore}% · ${headcount} employees`}
      placement="top"
      arrow
    >
      <Box sx={{
        bgcolor: rs.bg, borderRadius: 1.5,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        py: 1.25, px: 0.5, cursor: 'default', minHeight: 52,
        transition: 'transform 0.12s, box-shadow 0.12s',
        '&:hover': { transform: 'scale(1.07)', zIndex: 2, boxShadow: 2 },
      }}>
        <Typography variant="caption" fontWeight={800} color={rs.text} sx={{ lineHeight: 1 }}>
          {riskScore}%
        </Typography>
        <Typography sx={{ fontSize: '0.6rem', color: rs.text, opacity: 0.7, lineHeight: 1, mt: 0.25 }}>
          {headcount} emp
        </Typography>
      </Box>
    </Tooltip>
  );
}

function OverallBadge({ score }) {
  const level = getRiskLevel(score);
  const rs    = RISK[level];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Chip
        label={rs.label}
        size="small"
        sx={{
          bgcolor: rs.chipBg, color: rs.chipText,
          fontWeight: 700, fontSize: '0.7rem',
          height: 22, '& .MuiChip-label': { px: 0.75 },
        }}
      />
    </Box>
  );
}

const LEGEND_ITEMS = [
  { label: 'Low (< 30)',     ...RISK.low      },
  { label: 'Medium (30-50)', ...RISK.medium   },
  { label: 'High (50-70)',   ...RISK.high     },
  { label: 'Critical (70+)', ...RISK.critical },
];

// ── Main component ────────────────────────────────────────────────────────────
export default function AttritionHeatmap() {
  const { openWithMessage }   = useChatContext();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    workforceApi.getAttritionHeatmap()
      .then(setData)
      .catch(() => setData(STUB))
      .finally(() => setLoading(false));
  }, []);

  const handleRowAskAI = (row) => {
    const level   = getRiskLevel(row.overallRiskScore);
    const peakIdx = row.cells.reduce((max, c, i, arr) => c.riskScore > arr[max].riskScore ? i : max, 0);
    const peakBand = data?.tenureBands?.[peakIdx] ?? row.cells[peakIdx]?.tenureBand ?? '';
    openWithMessage(
      `${row.department} has a ${level} overall attrition risk score of ${row.overallRiskScore}/100. The highest risk segment is the "${peakBand}" tenure band (${row.cells[peakIdx]?.riskScore}% risk, ${row.cells[peakIdx]?.headcount} employees). What are the most likely causes and what retention actions should HR take?`
    );
  };

  const handleGlobalAskAI = () => {
    if (!data) return;
    const critical = (data.rows || []).filter(r => getRiskLevel(r.overallRiskScore) === 'critical').map(r => r.department);
    const high     = (data.rows || []).filter(r => getRiskLevel(r.overallRiskScore) === 'high').map(r => r.department);
    openWithMessage(
      `Our attrition risk heatmap shows: Critical risk — ${critical.join(', ') || 'none'}; High risk — ${high.join(', ') || 'none'}. ${data.riskSummary} Which departments should HR prioritise, and what is the most cost-effective retention strategy?`
    );
  };

  const tenureBands = data?.tenureBands ?? STUB.tenureBands;
  const rows        = data?.rows        ?? [];
  const cols        = tenureBands.length;

  return (
    <Card>
      <CardContent>
        {/* ── Header ── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: '#FFEBEE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Whatshot sx={{ fontSize: 18, color: '#C62828' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                Attrition Risk Heatmap
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Department × tenure band — risk score 0–100
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Ask AI about the overall risk picture">
            <IconButton size="small" onClick={handleGlobalAskAI} disabled={loading}
              sx={{ color: 'primary.main', opacity: 0.55, '&:hover': { opacity: 1 }, flexShrink: 0 }}>
              <AutoAwesome sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── AI Summary ── */}
        {!loading && data?.riskSummary && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, bgcolor: 'action.hover', borderRadius: 1.5, px: 1.5, py: 1, mb: 2 }}>
            <AutoAwesome sx={{ fontSize: 14, color: 'primary.main', mt: 0.2, flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {data.riskSummary}
            </Typography>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: `150px repeat(${cols}, 1fr) 76px 40px`,
              gap: 0.5,
              minWidth: 600,
            }}>
              {/* ── Column headers ── */}
              <Box /> {/* dept label corner */}
              {tenureBands.map((b) => (
                <Typography key={b} variant="caption" color="text.secondary" fontWeight={600}
                  sx={{ textAlign: 'center', fontSize: '0.68rem', py: 0.5, whiteSpace: 'nowrap' }}>
                  {b}
                </Typography>
              ))}
              <Typography variant="caption" color="text.secondary" fontWeight={600}
                sx={{ textAlign: 'center', fontSize: '0.68rem', py: 0.5 }}>
                Overall
              </Typography>
              <Box /> {/* Ask AI column header */}

              {/* ── Data rows ── */}
              {rows.map((row) => (
                <React.Fragment key={row.department}>
                  {/* Department label */}
                  <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                      {row.department}
                    </Typography>
                  </Box>

                  {/* Risk cells */}
                  {row.cells.map((cell, ci) => (
                    <HeatCell
                      key={ci}
                      riskScore={cell.riskScore}
                      headcount={cell.headcount}
                    />
                  ))}

                  {/* Overall badge */}
                  <OverallBadge score={row.overallRiskScore} />

                  {/* Per-row Ask AI */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Tooltip title={`Ask AI about ${row.department} attrition risk`}>
                      <IconButton size="small" onClick={() => handleRowAskAI(row)}
                        sx={{ color: 'primary.main', opacity: 0.45, '&:hover': { opacity: 1 }, p: 0.4 }}>
                        <AutoAwesome sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </React.Fragment>
              ))}
            </Box>

            {/* ── Legend ── */}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
              {LEGEND_ITEMS.map((item) => (
                <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 14, height: 14, bgcolor: item.bg, borderRadius: 0.5, flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
